import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, Target } from 'lucide-react';

const Game2048 = () => {
  const { t } = useTranslation();
  const [board, setBoard] = useState(Array(4).fill().map(() => Array(4).fill(0)));
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [moves, setMoves] = useState(0);

  const initializeBoard = () => {
    const newBoard = Array(4).fill().map(() => Array(4).fill(0));
    addRandomTile(newBoard);
    addRandomTile(newBoard);
    setBoard(newBoard);
    setScore(0);
    setGameOver(false);
    setWon(false);
    setMoves(0);
  };

  const addRandomTile = (boardState) => {
    const emptyCells = [];
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        if (boardState[i][j] === 0) {
          emptyCells.push({ x: i, y: j });
        }
      }
    }
    if (emptyCells.length > 0) {
      const randomCell = emptyCells[Math.floor(Math.random() * emptyCells.length)];
      boardState[randomCell.x][randomCell.y] = Math.random() < 0.9 ? 2 : 4;
    }
  };

  const moveTiles = useCallback((direction) => {
    if (gameOver) return;

    const newBoard = board.map(row => [...row]);
    let moved = false;
    let newScore = score;

    const moveRow = (row) => {
      const filtered = row.filter(cell => cell !== 0);
      for (let i = 0; i < filtered.length - 1; i++) {
        if (filtered[i] === filtered[i + 1]) {
          filtered[i] *= 2;
          newScore += filtered[i];
          filtered.splice(i + 1, 1);
        }
      }
      while (filtered.length < 4) {
        filtered.push(0);
      }
      return filtered;
    };

    const moveColumn = (col) => {
      const column = [newBoard[0][col], newBoard[1][col], newBoard[2][col], newBoard[3][col]];
      const filtered = column.filter(cell => cell !== 0);
      for (let i = 0; i < filtered.length - 1; i++) {
        if (filtered[i] === filtered[i + 1]) {
          filtered[i] *= 2;
          newScore += filtered[i];
          filtered.splice(i + 1, 1);
        }
      }
      while (filtered.length < 4) {
        filtered.push(0);
      }
      return filtered;
    };

    switch (direction) {
      case 'left':
        for (let i = 0; i < 4; i++) {
          const newRow = moveRow([...newBoard[i]]);
          if (JSON.stringify(newRow) !== JSON.stringify(newBoard[i])) {
            moved = true;
            newBoard[i] = newRow;
          }
        }
        break;
      case 'right':
        for (let i = 0; i < 4; i++) {
          const newRow = moveRow([...newBoard[i]].reverse()).reverse();
          if (JSON.stringify(newRow) !== JSON.stringify(newBoard[i])) {
            moved = true;
            newBoard[i] = newRow;
          }
        }
        break;
      case 'up':
        for (let j = 0; j < 4; j++) {
          const newCol = moveColumn(j);
          if (JSON.stringify(newCol) !== JSON.stringify([newBoard[0][j], newBoard[1][j], newBoard[2][j], newBoard[3][j]])) {
            moved = true;
            for (let i = 0; i < 4; i++) {
              newBoard[i][j] = newCol[i];
            }
          }
        }
        break;
      case 'down':
        for (let j = 0; j < 4; j++) {
          const newCol = moveColumn(j).reverse();
          if (JSON.stringify(newCol) !== JSON.stringify([newBoard[0][j], newBoard[1][j], newBoard[2][j], newBoard[3][j]])) {
            moved = true;
            for (let i = 0; i < 4; i++) {
              newBoard[i][j] = newCol[i];
            }
          }
        }
        break;
      default:
        break;
    }

    if (moved) {
      addRandomTile(newBoard);
      setBoard(newBoard);
      setScore(newScore);
      setMoves(prev => prev + 1);
      
      // Check for win
      if (!won && newBoard.some(row => row.some(cell => cell === 2048))) {
        setWon(true);
      }
      
      // Check for game over
      if (isGameOver(newBoard)) {
        setGameOver(true);
        if (newScore > highScore) {
          setHighScore(newScore);
        }
      }
    }
  }, [board, score, gameOver, won, highScore]);

  const isGameOver = (boardState) => {
    // Check if there are empty cells
    if (boardState.some(row => row.some(cell => cell === 0))) {
      return false;
    }
    
    // Check if any moves are possible
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        const current = boardState[i][j];
        if (
          (i < 3 && boardState[i + 1][j] === current) ||
          (j < 3 && boardState[i][j + 1] === current)
        ) {
          return false;
        }
      }
    }
    return true;
  };

  useEffect(() => {
    initializeBoard();
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameOver) return;

      switch (e.code) {
        case 'ArrowLeft':
          e.preventDefault();
          moveTiles('left');
          break;
        case 'ArrowRight':
          e.preventDefault();
          moveTiles('right');
          break;
        case 'ArrowUp':
          e.preventDefault();
          moveTiles('up');
          break;
        case 'ArrowDown':
          e.preventDefault();
          moveTiles('down');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [moveTiles, gameOver]);

  const getTileColor = (value) => {
    const colors = {
      2: 'bg-gray-400',
      4: 'bg-gray-300',
      8: 'bg-red-400',
      16: 'bg-orange-400',
      32: 'bg-yellow-400',
      64: 'bg-yellow-300',
      128: 'bg-green-400',
      256: 'bg-green-300',
      512: 'bg-blue-400',
      1024: 'bg-blue-300',
      2048: 'bg-purple-400'
    };
    return colors[value] || 'bg-gray-500';
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">High Score</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{highScore}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Target className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">Moves</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{moves}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={initializeBoard}
              className="cyber-button text-sm px-4 py-2"
            >
              New Game
            </button>
          </div>
        </div>
      </div>

      {/* Win Message */}
      {won && !gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-green/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-green mb-4">
            You Won! 🎉
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            You reached 2048! Keep playing to get a higher score.
          </p>
        </motion.div>
      )}

      {/* Game Over Message */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-red/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-red mb-4">
            Game Over!
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Final Score: {score} | Moves: {moves}
          </p>
          <button
            onClick={initializeBoard}
            className="cyber-button text-lg px-6 py-3"
          >
            Play Again
          </button>
        </motion.div>
      )}

      {/* Game Board */}
      <div className="cyber-card p-6">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-cyber font-bold text-white mb-2">
            2048
          </h3>
          <p className="text-gray-400 font-tech">
            Use arrow keys to move tiles. Combine same numbers to reach 2048!
          </p>
        </div>
        
        <div className="flex justify-center">
          <div className="grid grid-cols-4 gap-2 bg-dark-200 p-4 rounded-lg">
            {board.map((row, i) =>
              row.map((cell, j) => (
                <motion.div
                  key={`${i}-${j}`}
                  className={`w-20 h-20 flex items-center justify-center rounded-lg font-cyber font-bold text-2xl ${
                    cell === 0 ? 'bg-dark-100' : getTileColor(cell)
                  } ${cell === 0 ? 'text-transparent' : 'text-white'}`}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: (i * 4 + j) * 0.05 }}
                >
                  {cell !== 0 && cell}
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Controls Info */}
      <div className="cyber-card p-6 mt-8">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            Controls
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-gray-300 font-tech">
            <div>
              <p className="font-bold text-neon-blue">↑</p>
              <p>Move Up</p>
            </div>
            <div>
              <p className="font-bold text-neon-green">↓</p>
              <p>Move Down</p>
            </div>
            <div>
              <p className="font-bold text-neon-pink">←</p>
              <p>Move Left</p>
            </div>
            <div>
              <p className="font-bold text-neon-yellow">→</p>
              <p>Move Right</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Game2048;
